import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useBoardsContext } from '../context/BoardsContext'
import useBoardMutations from '../hooks/useBoardMutations'
import CommentSection from './CommentSection'

function timeAgo(dateString) {
  if (!dateString) return null
  const seconds = Math.floor((Date.now() - new Date(dateString)) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  return `${months}mo ago`
}

function Eyebrow({ children }) {
  return (
    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
      {children}
    </div>
  )
}

function OverflowMenu({ items }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  if (!items.length) return null

  return (
    <div className="relative" ref={ref}>
      <button
        className="kb-card-detail-more"
        onClick={() => setOpen(o => !o)}
        title="More actions"
      >
        &hellip;
      </button>
      {open && (
        <div className="kb-card-detail-more-menu">
          {items.map((item, i) => (
            <button
              key={i}
              className={item.danger ? 'kb-card-detail-more-menu-item kb-card-detail-more-menu-item--danger' : 'kb-card-detail-more-menu-item'}
              onClick={() => { setOpen(false); item.onClick() }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function CardDetail({ card, columnId, onClose }) {
  const { canEdit, selectedBoardId } = useBoardsContext()
  const { deleteCard } = useBoardMutations(selectedBoardId)
  const queryClient = useQueryClient()

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const jobDetailsQuery = useQuery({
    queryKey: ['kanbanCardJobDetails', card.id],
    queryFn: async () => {
      const { FetchRequest } = await import('@rails/request.js')
      const request = new FetchRequest('get', `/kanban_cards/${card.id}/job_details`, {
        headers: { Accept: 'application/json' }
      })
      const response = await request.perform()
      return response.json
    }
  })

  const updateJobMutation = useMutation({
    mutationFn: async (data) => {
      const { FetchRequest } = await import('@rails/request.js')
      const request = new FetchRequest('put', `/kanban_cards/${card.id}/job_details`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      const response = await request.perform()
      return response.json
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanbanCardJobDetails', card.id] })
      queryClient.invalidateQueries({ queryKey: ['kanbanColumn', columnId, 'cards'] })
    }
  })

  const details = jobDetailsQuery.data || {}

  const handleDelete = () => {
    if (window.confirm('Remove this card from the board?')) {
      deleteCard.mutate({ id: card.id, columnId })
      onClose()
    }
  }

  const overflowItems = canEdit
    ? [{ label: 'Remove card from board', onClick: handleDelete, danger: true }]
    : []

  const movedAgo = card.column_changed_at ? timeAgo(card.column_changed_at) : null
  const statusName = details.job_status_name || card.job_status_name

  return (
    <div className="kb-modal kb-card-detail fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="kb-card-detail-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="kb-card-detail-header">
          <div>
            <div className="kb-card-detail-eyebrow">
              {card.job_custom_id && <span>{card.job_custom_id}</span>}
              {statusName && <span className="kb-card-detail-eyebrow-sep">&middot;</span>}
              {statusName && <span className="kb-card-detail-eyebrow-status">{statusName}</span>}
              {movedAgo && <span className="kb-card-detail-eyebrow-sep">&middot;</span>}
              {movedAgo && <span>{movedAgo}</span>}
            </div>
            <h3 className="kb-card-detail-title">{card.job_name}</h3>
          </div>
          <button className="kb-card-detail-close" onClick={onClose} title="Close">
            &times;
          </button>
        </div>

        <div className="kb-card-detail-body">
          <aside className="kb-card-detail-left">
            {jobDetailsQuery.isLoading ? (
              <div className="text-sm text-gray-400">Loading...</div>
            ) : (
              <>
                <section>
                  <Eyebrow>Status</Eyebrow>
                  <StatusControl details={details} canEdit={canEdit} onUpdate={updateJobMutation} />
                </section>

                <section>
                  <Eyebrow>Tags</Eyebrow>
                  <TagControl details={details} canEdit={canEdit} onUpdate={updateJobMutation} />
                </section>

                <div className="kb-card-detail-left-foot">
                  <a
                    href={`/jobs/${card.job_id}`}
                    className="kb-card-detail-open-job"
                  >
                    Open Job &rarr;
                  </a>
                </div>
              </>
            )}
          </aside>

          <div className="kb-card-detail-divider" />

          <section className="kb-card-detail-right">
            <Eyebrow>Job Comments</Eyebrow>
            <CommentSection cardId={card.id} />
          </section>
        </div>

        <div className="kb-card-detail-footer">
          <div className="kb-card-detail-footer-hint">Press Esc to close</div>
          <OverflowMenu items={overflowItems} />
        </div>
      </div>
    </div>
  )
}

function StatusControl({ details, canEdit, onUpdate }) {
  if (!details.available_statuses) return null

  const handleChange = (e) => {
    onUpdate.mutate({ job_status_id: Number(e.target.value) })
  }

  if (canEdit && details.can_change_status) {
    return (
      <select
        className="form-select block w-full rounded-md border-gray-300 shadow-sm text-sm"
        value={details.job_status_id || ''}
        onChange={handleChange}
        disabled={onUpdate.isPending}
      >
        {details.available_statuses.map(s => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>
    )
  }

  return (
    <span className="inline-block px-2 py-1 rounded text-sm bg-blue-50 text-blue-700">
      {details.job_status_name}
    </span>
  )
}

function TagControl({ details, canEdit, onUpdate }) {
  const [newTag, setNewTag] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)

  if (!details.tag_list) return null

  const handleRemoveTag = (tag) => {
    const updated = details.tag_list.filter(t => t !== tag)
    onUpdate.mutate({ tag_list: updated })
  }

  const handleAddTag = (tag) => {
    const trimmed = tag.trim()
    if (!trimmed || details.tag_list.includes(trimmed)) return
    onUpdate.mutate({ tag_list: [...details.tag_list, trimmed] })
    setNewTag('')
    setShowSuggestions(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag(newTag)
    }
    if (e.key === 'Escape') {
      setNewTag('')
      setShowSuggestions(false)
    }
  }

  const filteredSuggestions = (details.existing_tags || []).filter(
    t => t.toLowerCase().includes(newTag.toLowerCase()) && !details.tag_list.includes(t)
  ).slice(0, 8)

  return (
    <>
      <div className="flex flex-wrap gap-1">
        {details.tag_list.map(tag => (
          <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm bg-purple-50 text-purple-700">
            {tag}
            {canEdit && (
              <button
                className="text-purple-400 hover:text-purple-700 text-xs leading-none"
                onClick={() => handleRemoveTag(tag)}
              >
                &times;
              </button>
            )}
          </span>
        ))}
        {details.tag_list.length === 0 && (
          <span className="text-xs text-gray-400 italic">no tags</span>
        )}
      </div>
      {canEdit && (
        <div className="mt-2 relative">
          <input
            type="text"
            className="form-input w-full rounded-md border-gray-300 shadow-sm text-sm"
            placeholder="Add a tag..."
            value={newTag}
            onChange={(e) => { setNewTag(e.target.value); setShowSuggestions(true) }}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          />
          {showSuggestions && newTag.length > 0 && filteredSuggestions.length > 0 && (
            <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto">
              {filteredSuggestions.map(tag => (
                <button
                  key={tag}
                  className="block w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                  onMouseDown={(e) => { e.preventDefault(); handleAddTag(tag) }}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}

CardDetail.propTypes = {
  card: PropTypes.object.isRequired,
  columnId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired
}

export default CardDetail
